import { Component, OnInit } from '@angular/core';
import {
  TUI_VALIDATION_ERRORS,
  TuiFieldErrorPipeModule,
  TuiInputModule,
  TuiInputPhoneModule,
  TuiMultiSelectModule,
  TuiTextareaModule,
} from '@taiga-ui/kit';
import {
  TuiAlertModule,
  TuiAlertService,
  TuiDataListModule,
  TuiErrorModule,
  TuiLoaderModule,
  TuiSvgModule,
} from '@taiga-ui/core';
import { TuiDataListWrapperModule, TuiSelectModule } from '@taiga-ui/kit';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TuiButtonModule, TuiTextfieldControllerModule } from '@taiga-ui/core';
import { TuiInputDateModule } from '@taiga-ui/kit';
import { TuiRadioLabeledModule } from '@taiga-ui/kit';
import { AppUtils } from '../../../core/config/app.utils';
import { ActivatedRoute, Router } from '@angular/router';
import { MemberService } from '../../../core/services/member.service';
import { AsyncPipe, NgIf } from '@angular/common';
import {
  TUI_DATE_FORMAT,
  TUI_DATE_SEPARATOR,
  TUI_DEFAULT_MATCHER,
  TuiBooleanHandler,
  TuiDay,
  tuiPure,
} from '@taiga-ui/cdk';
import { AuthService } from '../../../modules/auth/services/auth.service';
import { SETTINGS } from '../../../core/config/common.settings';
import { PackageService } from '../../../core/services/package.service';

@Component({
  selector: 'app-add-member',
  standalone: true,
  imports: [
    TuiInputModule,
    TuiSelectModule,
    TuiDataListModule,
    TuiDataListWrapperModule,
    TuiInputPhoneModule,
    FormsModule,
    ReactiveFormsModule,
    TuiTextfieldControllerModule,
    TuiButtonModule,
    TuiInputDateModule,
    TuiRadioLabeledModule,
    TuiTextareaModule,
    TuiAlertModule,
    NgIf,
    AsyncPipe,
    TuiErrorModule,
    TuiFieldErrorPipeModule,
    TuiLoaderModule,
    TuiMultiSelectModule,
    FormsModule,
    ReactiveFormsModule,
    TuiMultiSelectModule,
    TuiDataListModule,
    TuiDataListWrapperModule,
    TuiSvgModule,
  ],
  providers: [
    MemberService,
    PackageService,
    {
      provide: TUI_VALIDATION_ERRORS,
      useValue: { required: 'Required', email: 'Enter a valid email' },
    },
    { provide: TUI_DATE_FORMAT, useValue: 'YMD' },
    { provide: TUI_DATE_SEPARATOR, useValue: '/' },
  ],
  templateUrl: './add-member.component.html',
  styleUrl: './add-member.component.scss',
})
export class AddMemberComponent implements OnInit {
  status = 'add';
  memberId: string | null = null;
  MemberData: any;
  memberForm!: FormGroup;
  formErrors: any;
  previous = 'view';
  isLoading = false;

  user: any;
  packageOptions: any[] = [];
  search: string | null = '';
  profilePictureObj: { url: string; name: string } | null = null;

  packages: readonly string[] = [
    'Personal Training',
    'Gym Only Use',
    'Combat Training',
    'Plan and Assessment',
  ];
  sessions: readonly string[] = ['PT', 'Gym'];

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private memberService: MemberService,
    private packageService: PackageService,
    private authService: AuthService,
    private alertService: TuiAlertService
  ) {}

  ngOnInit(): void {
    this.initMemberForm();

    this.route.params.subscribe((params) => {
      this.previous = params['status'];
      this.user = this.authService.getLoggedInUser();
      this.memberId = this.route.snapshot.paramMap.get('id');
      this.listPackages();
    });
  }

  private initMemberForm(): void {
    this.memberForm = this.fb.group({
      email: [null, [Validators.required, Validators.email]],
      membershipId: [null, Validators.required],
      fullname: [
        null,
        [Validators.required, Validators.pattern(/^[a-z ,.'-]+$/i)],
      ],
      registrationDate: [null, Validators.required],
      address: [null, Validators.required],
      mobileNo: [
        null,
        [Validators.required, Validators.pattern(/^[0-9]{0,16}$/)],
      ],
      gender: [null, Validators.required],
      dob: [null, Validators.required],
      programs: [null, Validators.required],
      package: [null, Validators.required],
      session: [null, Validators.required],
      emergencyContact: this.fb.group({
        name: [
          null,
          [Validators.required, Validators.pattern(/^[a-z ,.'-]+$/i)],
        ],
        mobileNo: [
          null,
          [Validators.required, Validators.pattern(/^[0-9]{0,16}$/)],
        ],
      }),
      profilePicture: [null],
    });

    this.memberForm.valueChanges.subscribe(() => {
      this.formErrors = AppUtils.getFormErrors(
        this.memberForm,
        this.formErrors
      );
    });
  }

  private async listPackages() {
    this.packageOptions = await this.packageService.getAllPackageOptions();
    if (this.user.userType === SETTINGS.USERS.MEMBER) {
      this.setValueOnMemberLogged();
    } else if (this.memberId) {
      this.setValueOnAdminLogged();
    }
  }

  private setValueOnAdminLogged() {
    this.isLoading = true;
    this.memberService.getOneMemberById(this.memberId).then(
      (res: any) => {
        this.isLoading = false;
        this.status = 'edit';
        this.MemberData = res;

        this.memberForm.patchValue(this.MemberData);

        const fullName = `${this.MemberData?.firstname || ''} ${
          this.MemberData?.lastname || ''
        }`.trim();
        this.memberForm.get('fullname')?.patchValue(fullName);

        this.memberForm
          .get('package')
          ?.patchValue(
            this.packageOptions.find(
              (val) =>
                val._id.toString() === this.MemberData.package._id.toString()
            )
          );

        this.memberForm
          .get('dob')
          ?.patchValue(this.parseBackendDate(this.MemberData.dob));
        this.memberForm
          .get('registrationDate')
          ?.patchValue(this.parseBackendDate(this.MemberData.registrationDate));
        this.memberForm.get('session')?.patchValue(this.MemberData.session);

        if (this.MemberData.programs) {
          this.memberForm.get('programs')?.patchValue(this.MemberData.programs);
        }

        if (this.MemberData.profilePicture?.length) {
          this.profilePictureObj = this.MemberData.profilePicture[0];
        }
      },
      () => (this.isLoading = false)
    );
  }

  private setValueOnMemberLogged() {
    this.isLoading = true;
    this.memberService.getOneMemberByMemberLogged().then(
      (res: any) => {
        this.isLoading = false;
        this.status = 'edit';
        this.MemberData = res;
        this.memberForm.disable();
        this.memberForm.get('address')?.enable();
        this.memberForm.get('mobileNo')?.enable();

        this.memberForm.patchValue(this.MemberData);

        const fullName = `${this.MemberData?.firstname || ''} ${
          this.MemberData?.lastname || ''
        }`.trim();
        this.memberForm.get('fullname')?.patchValue(fullName);

        this.memberForm
          .get('package')
          ?.patchValue(
            this.packageOptions.find(
              (val) =>
                val._id.toString() === this.MemberData.package._id.toString()
            )
          );

        this.memberForm
          .get('dob')
          ?.patchValue(this.parseBackendDate(this.MemberData.dob));
        this.memberForm
          .get('registrationDate')
          ?.patchValue(this.parseBackendDate(this.MemberData.registrationDate));
        this.memberForm.get('session')?.patchValue(this.MemberData.session);

        if (this.MemberData.programs) {
          this.memberForm.get('programs')?.patchValue(this.MemberData.programs);
        }

        if (this.MemberData.profilePicture?.length) {
          this.profilePictureObj = this.MemberData.profilePicture[0];
        }
      },
      () => (this.isLoading = false)
    );
  }

  private parseBackendDate(dateString: string): TuiDay {
    const date = new Date(dateString);
    return new TuiDay(date.getFullYear(), date.getMonth(), date.getDate());
  }

  @tuiPure
  filter(search: string | null): readonly string[] {
    return this.packages.filter((item) =>
      TUI_DEFAULT_MATCHER(item, search || '')
    );
  }

  tagValidator: TuiBooleanHandler<string> = (tag) => !tag.startsWith('Han');

  private splitFullName(fullname: string | null | undefined) {
    const safe = (fullname || '').trim().replace(/\s+/g, ' ');
    if (!safe) return { firstname: '', lastname: '' };
    const parts = safe.split(' ');
    const lastname = parts.pop() || '';
    const firstname = parts.join(' ');
    return { firstname, lastname };
  }

  async submit() {
    if (!this.memberForm.valid) return this.memberForm.markAllAsTouched();

    this.isLoading = true;
    try {
      const formData = this.memberForm.getRawValue();
      const { firstname, lastname } = this.splitFullName(formData.fullname);
      const payload = {
        ...formData,
        firstname,
        lastname,
        package: formData.package._id,
        session: formData.session,
        profilePicture: this.profilePictureObj ? [this.profilePictureObj] : [],
      };

      await this.memberService.createMember(payload);
      this.alertService
        .open('Member created successfully!', { status: 'success' })
        .subscribe();
      this.navigateToView();
    } catch (error: any) {
      this.handleError(error, 'create');
    } finally {
      this.isLoading = false;
    }
  }

  async onUpdate() {
    if (!this.memberForm.valid) return this.memberForm.markAllAsTouched();

    try {
      const updateFn =
        this.user.userType === SETTINGS.USERS.ADMIN
          ? this.memberUpdateByLoggedAdmin.bind(this)
          : this.memberUpdateByLoggedMember.bind(this);

      await updateFn();
      this.alertService
        .open('Member updated successfully!', { status: 'success' })
        .subscribe();
      this.cancel();
    } catch (error: any) {
      this.handleError(error, 'update');
    }
  }

  private async memberUpdateByLoggedAdmin() {
    const formValue = this.memberForm.value;
    const { firstname, lastname } = this.splitFullName(formValue.fullname);

    await this.memberService.updateMember({
      _id: this.memberId,
      ...formValue,
      firstname,
      lastname,
      package: formValue.package._id,
      session: formValue.session,
      profilePicture: this.profilePictureObj ? [this.profilePictureObj] : [],
    });
  }

  private async memberUpdateByLoggedMember() {
    const formValue = this.memberForm.value;
    const { firstname, lastname } = this.splitFullName(formValue.fullname);

    await this.memberService.updateMemberByLoggedMember({
      _id: this.memberId,
      ...formValue,
      firstname,
      lastname,
      package: formValue.package._id,
      session: formValue.session,
      profilePicture: this.profilePictureObj ? [this.profilePictureObj] : [],
    });
  }

  private handleError(error: any, context: 'create' | 'update') {
    console.error(`${context} member error:`, error);

    let errorMsg = error.message || 'Something went wrong.';

    if (error.memberIdCheck) {
      errorMsg =
        'This Membership ID already exists. Please choose another one.';
    } else if (error.emailCheck) {
      errorMsg = 'This email already exists. Please try again.';
    } else if (error.oldPwdCheck) {
      errorMsg = 'The old password you entered is incorrect.';
    }

    this.alertService.open(errorMsg, { status: 'error' }).subscribe();
  }

  onFileSelected(event: Event) {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;

    // File size validation: max 5MB
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      this.alertService
        .open('Profile picture too large. Maximum size is 5MB.', {
          status: 'error',
        })
        .subscribe();
      (event.target as HTMLInputElement).value = ''; // Clear the input
      return;
    }

    const reader = new FileReader();
    reader.onload = () =>
      (this.profilePictureObj = {
        url: reader.result as string,
        name: file.name,
      });
    reader.readAsDataURL(file);
  }

  removeProfilePicture() {
    this.profilePictureObj = null;
  }

  cancel() {
    if (this.previous === 'single') this.navigateToSingleView();
    else this.navigateToView();
  }

  private navigateToView() {
    if (this.user.userType === SETTINGS.USERS.ADMIN)
      this.router.navigate(['admin/members']);
  }

  private navigateToSingleView() {
    if (this.user.userType === SETTINGS.USERS.ADMIN)
      this.router.navigate([`admin/view-member/${this.memberId}`]);
    else this.router.navigate([`member/profile`]);
  }
}
